import { Injectable } from '@angular/core';

type ApplyHandler<T> = (componentRef: unknown, payload: T) => void;

interface DeferredApplyEntry<T> {
  componentRef?: unknown;
  apply?: ApplyHandler<T>;
  payload?: T;
  hasPayload?: boolean;
}

@Injectable({
  providedIn: 'root'
})
export class DeferredApplyService {
  private readonly entries = new Map<string, DeferredApplyEntry<unknown>>();

  /*
  Queues a payload to be applied to a component once it is registered.
  If the component is already registered, the payload is applied immediately.
  */
  queue<T>(key: string, payload: T): void {
    const entry = (this.entries.get(key) as DeferredApplyEntry<T> | undefined) ?? {};
    entry.payload = payload;
    entry.hasPayload = true;
    this.entries.set(key, entry as DeferredApplyEntry<unknown>);
    this.tryApply(key);
  }

  /*
  Registers a component with an apply handler.
  If there is a queued payload for this component, it is applied immediately.
  */
  register<T>(key: string, componentRef: unknown, apply: ApplyHandler<T>): void {
    const entry = (this.entries.get(key) as DeferredApplyEntry<T> | undefined) ?? {};
    entry.componentRef = componentRef;
    entry.apply = apply;
    this.entries.set(key, entry as DeferredApplyEntry<unknown>);
    this.tryApply(key);
  }

  clear(key: string): void {
    this.entries.delete(key);
  }

  private tryApply(key: string): void {
    const entry = this.entries.get(key);
    if (!entry || !entry.componentRef || !entry.apply || !entry.hasPayload) {
      return;
    }

    (entry.apply as ApplyHandler<unknown>)(entry.componentRef, entry.payload);
    this.entries.delete(key);
  }
}
